<script setup>
import {ref, onMounted, computed} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
import {showMessage} from "@/utils/showMessage.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {DataTable, Column, Tag} from 'primevue';
import SortIcon from "@/components/datatable/sortIcon.vue";

const ordersStore = useMainOrdersStore();
const configStore = useConfigStore();
const {fetching, setFetching} = useLoadingFlags();

const props = defineProps({
  user: {type: Object, required: true},
  maximized: {type: Boolean, required: true, default: false}
});

const ratedOrders = ref([]);

const sortKey = ref(null);      // 'id' | 'type' | 'adminName' | 'lastUpdate' | ...
const sortOrder = ref(1);        // 1 = asc, -1 = desc


// Helper function to convert null/undefined to empty string
const safeValue = (val) => {
  if (val === null || val === undefined) return '';
  return val;
};

// sorted data
const displayedOrders = computed(() => {
  if (!sortKey.value) return ratedOrders.value;

  return [...ratedOrders.value].sort((a, b) => {
    let A = safeValue(a[sortKey.value]);
    let B = safeValue(b[sortKey.value]);

    // for columns that are numbers
    if (sortKey.value === 'lastUpdate' || sortKey.value === 'id' || sortKey.value === 'rating') {
      A = Number(A) || 0;
      B = Number(B) || 0;
    } else {
      // for strings
      A = String(A).toLowerCase();
      B = String(B).toLowerCase();
    }

    if (A < B) return -1 * sortOrder.value;
    if (A > B) return 1 * sortOrder.value;
    return 0;
  });
});

// click on header
const toggleSort = (key) => {
  if (sortKey.value === key) {
    if (sortOrder.value === 1) sortOrder.value = -1;
    else {
      sortKey.value = null;
      sortOrder.value = 1;
    }
  } else {
    sortKey.value = key;
    sortOrder.value = 1;
  }
};

const getUserRatingAndTags = async () => {
  try {
    setFetching(true)
    const res = await ordersStore.action_getUserRatingAndTags({id: props.user.id})
    if (res.result === true) {
      ratedOrders.value = res.data
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    setFetching(false)
  }
}

//hooks
onMounted(() => {
  getUserRatingAndTags()
})
</script>

<template>
  <div :class="['h-[calc(100vh-100px)] overflow-hidden', maximized ? 'w-full' : 'min-w-[48rem] max-w-3xl']">
    <div v-if="fetching" class="flex h-full items-center justify-center text-2xl text-slate-400">
      loading
    </div>

    <div v-else class="overflow-auto h-full">
      <table class="w-full text-xs text-slate-300 border-separate border-spacing-0">
        <thead class="sticky top-0 bg-slate-800 shadow-md z-10">
        <tr>
          <th @click="toggleSort('id')"
              class="px-2 py-1 h-8 text-xs font-bold text-white cursor-pointer hover:text-slate-300 border-b border-slate-700 whitespace-nowrap select-none">
            <div class="flex flex-row items-center gap-2">
              <span>ID</span>
              <SortIcon v-if="sortKey === 'id'" :sortOrder="sortOrder"/>
            </div>
          </th>
          <th @click="toggleSort('type')"
              class="px-2 py-1 h-8 text-xs font-bold text-white cursor-pointer hover:text-slate-300 border-b border-slate-700 whitespace-nowrap select-none">
            <div class="flex flex-row items-center gap-2">
              <span>Type</span>
              <SortIcon v-if="sortKey === 'type'" :sortOrder="sortOrder"/>
            </div>
          </th>
          <th @click="toggleSort('adminName')"
              class="px-2 py-1 h-8 text-xs font-bold text-white cursor-pointer hover:text-slate-300 border-b border-slate-700 whitespace-nowrap select-none">
            <div class="flex flex-row items-center gap-2">
              <span>Admin Name</span>
              <SortIcon v-if="sortKey === 'adminName'" :sortOrder="sortOrder"/>
            </div>
          </th>
          <th @click="toggleSort('rating')"
              class="px-2 py-1 h-8 text-xs font-bold text-white cursor-pointer hover:text-slate-300 border-b border-slate-700 whitespace-nowrap select-none">
            <div class="flex flex-row items-center gap-2">
              <span>Rating</span>
              <SortIcon v-if="sortKey === 'rating'" :sortOrder="sortOrder"/>
            </div>
          </th>
          <th @click="toggleSort('tags')"
              class="px-2 py-1 h-8 text-xs font-bold text-white cursor-pointer hover:text-slate-300 border-b border-slate-700 whitespace-nowrap select-none">
            <div class="flex flex-row items-center gap-2">
              <span>Tags</span>
              <SortIcon v-if="sortKey === 'tags'" :sortOrder="sortOrder"/>
            </div>
          </th>
          <th @click="toggleSort('lastUpdate')"
              class="px-2 py-1 h-8 text-xs font-bold text-white cursor-pointer hover:text-slate-300 border-b border-slate-700 whitespace-nowrap select-none">
            <div class="flex flex-row items-center gap-2">
              <span>Last Update</span>
              <SortIcon v-if="sortKey === 'lastUpdate'" :sortOrder="sortOrder"/>
            </div>
          </th>
        </tr>
        </thead>
        <tbody>
        <template v-for="(order, index) in displayedOrders" :key="order.id">
          <!-- first line: main data -->
          <tr :class="index % 2 === 0 ? 'bg-[#1A2941]' : 'bg-[#121E31]'">
            <td class="px-2 py-1 font-medium">{{ order.id }}</td>
            <td class="px-2 py-1">{{ order.type }}</td>
            <td class="px-2 py-1">{{ order.adminName }}</td>
            <td class="px-2 py-1">
              <div class="flex flex-row items-center gap-0.5 h-full w-full px-[0.5rem] py-[0.375rem]">
                <span class="flex flex-row items-end"> {{ order.rating }} </span>⭐
              </div>
            </td>
            <td class="px-2 py-1">
              <div class="flex flex-wrap gap-2">
                <Tag v-for="tag in order.tags" :severity="configStore.tags[tag] === 1 ? 'success' : 'danger'"
                     :value="tag" class="!font-normal !text-xs"></Tag>
              </div>
            </td>
            <td class="px-2 py-1">
              <span class="block w-full max-w-max whitespace-nowrap"> {{
                  gregorianDate(order.lastUpdate, 'DD MMM HH:mm')
                }} </span>
            </td>
          </tr>

          <!-- second line: just review -->
          <tr v-if="order.review" :class="index % 2 === 0 ? 'bg-[#1A2941]' : 'bg-[#121E31]'">
            <td colspan="6" class="px-2 py-1">
              <div
                  class="text-sm leading-relaxed text-slate-300 whitespace-pre-wrap break-words pt-2 border-t border-slate-700/70">
                {{ order.review }}
              </div>
            </td>
          </tr>
        </template>
        </tbody>
      </table>

      <div v-if="!ratedOrders.length" class="flex h-full items-center justify-center text-slate-500">
        No History
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflow-auto {
  scrollbar-width: thin;
  scrollbar-color: #475569 #1e293b;
}
</style>